// PounceLeadsPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Pounce ki saari leads ka page (/leads) — Pounce agent ke "New Leads" tile se
// khulta hai. Upar search, phir status ke tabs, phir har lead ka card.
//
// Data: GET /pounce/leads, infinite scrolling in batches of 30, socket ke
// "pounce_lead_updated" pe non-destructive merge taaki scroll jump na ho.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from "react";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getPounceLeadsPaged } from "../../Store/apiStore";
import HeaderBar from "../HeaderBar/HeaderBar";
import styles from "./PounceLeadsPage.module.css";
import PounceLeadsTabs from "./PounceLeadsTabs/PounceLeadsTabs";
import PounceLeadCard from "./PounceLeadCard/PounceLeadCard";
import PounceLeadCardSkeleton from "./PounceLeadCardSkeleton/PounceLeadCardSkeleton";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import { usePounceLeadEvents } from "./pounceLeadShared";
import PounceToggle from "./PounceToggle/PounceToggle";

const PAGE_SIZE = 100;

const TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "calling", label: "Calling" },
  { key: "qualified", label: "Qualified" },
];

const PounceLeadsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null); // null = pehli baar load ho raha hai
  const [failed, setFailed] = useState(false);
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    new: 0,
    calling: 0,
    qualified: 0,
  });
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);

  // ?tab=qualified jaise link (agent page ke tiles) seedha us tab pe kholte hain.
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "all",
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const sentinelRef = useRef(null);
  const isFetchingRef = useRef(false);
  const requestIdRef = useRef(0);
  const leadsRef = useRef([]);
  leadsRef.current = leads || [];

  // Footer ke "Home" ke liye: jis agent se user yahan aaya.
  const agentId =
    sessionStorage.getItem("SelectAgentId") ||
    localStorage.getItem("SelectAgentId") ||
    "";
  const handleBack = () => {
    if (location.state?.fromBulkUploadSuccess) {
      navigate("/fast-agent-detail", { replace: true });
      return;
    }

    navigate(-1); // baaki sab cases mein current Back behavior
  };
  // Debounce search input (300ms) taaki har keystroke pe request na daude
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Pehla page fetch karna (initial load, tab switch, ya search badalne par)
  const fetchInitial = useCallback(async (tab, query) => {
    const reqId = ++requestIdRef.current;
    isFetchingRef.current = true;
    setFailed(false);
    setLoadMoreError(false);

    try {
      const p = {
        limit: PAGE_SIZE,
        offset: 0,
      };
      if (tab && tab !== "all") p.status = tab;
      if (query) p.search = query;

      const res = await getPounceLeadsPaged(p);
      if (reqId !== requestIdRef.current) return; // Outdated response ignore karo

      const rows = res.data || [];
      setLeads(rows);
      if (res.tabCounts) setTabCounts(res.tabCounts);
      const total = typeof res.total === "number" ? res.total : rows.length;
      setHasMore(rows.length < total && rows.length >= PAGE_SIZE);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setFailed(err?.response?.status === 403 ? "forbidden" : true);
    } finally {
      if (reqId === requestIdRef.current) {
        isFetchingRef.current = false;
      }
    }
  }, []);

  // Agla batch fetch karna jab sentinel screen pe aaye
  const fetchMore = useCallback(async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(false);

    const reqId = requestIdRef.current;
    const currentOffset = leadsRef.current.length;

    try {
      const p = {
        limit: PAGE_SIZE,
        offset: currentOffset,
      };
      if (activeTab && activeTab !== "all") p.status = activeTab;
      if (debouncedSearch) p.search = debouncedSearch;

      const res = await getPounceLeadsPaged(p);
      if (reqId !== requestIdRef.current) return;

      const rows = res.data || [];
      if (rows.length === 0) {
        setHasMore(false);
      } else {
        setLeads((prev) => {
          const existingIds = new Set((prev || []).map((l) => l.id));
          const uniqueNew = rows.filter((l) => !existingIds.has(l.id));
          return [...(prev || []), ...uniqueNew];
        });
        const total = typeof res.total === "number" ? res.total : 0;
        setHasMore(
          currentOffset + rows.length < total && rows.length >= PAGE_SIZE,
        );
      }
      if (res.tabCounts) setTabCounts(res.tabCounts);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setLoadMoreError(true);
    } finally {
      if (reqId === requestIdRef.current) {
        isFetchingRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [hasMore, activeTab, debouncedSearch]);

  // Tab switch ya debouncedSearch badalne par reset & reload
  useEffect(() => {
    const wrapper = document.querySelector(".custom-refresh-wrapper");
    const style = wrapper ? window.getComputedStyle(wrapper) : null;

    const wrapperIsScrollable =
      wrapper &&
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      wrapper.scrollHeight > wrapper.clientHeight + 1;

    if (wrapperIsScrollable) {
      wrapper.scrollTo({ top: 0, behavior: "auto" });
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }

    setLeads(null);
    fetchInitial(activeTab, debouncedSearch);
  }, [activeTab, debouncedSearch, fetchInitial]);

  // Live Socket updates: scroll jump kiye bina top rows ko update/merge karo
  const handleSocketRefresh = useCallback(async () => {
    const reqId = requestIdRef.current;
    try {
      const p = {
        limit: PAGE_SIZE,
        offset: 0,
      };
      if (activeTab && activeTab !== "all") p.status = activeTab;
      if (debouncedSearch) p.search = debouncedSearch;

      const res = await getPounceLeadsPaged(p);
      if (reqId !== requestIdRef.current) return;

      const freshRows = res.data || [];
      if (freshRows.length > 0) {
        setLeads((prev) => {
          if (!prev) return freshRows;
          const map = new Map(prev.map((l) => [l.id, l]));
          freshRows.forEach((fl) => {
            map.set(fl.id, fl);
          });
          return Array.from(map.values()).sort((a, b) => {
            const timeA = new Date(a.createdAt || 0).getTime();
            const timeB = new Date(b.createdAt || 0).getTime();
            if (timeB !== timeA) return timeB - timeA;
            return (b.id || 0) - (a.id || 0);
          });
        });
      }
      if (res.tabCounts) setTabCounts(res.tabCounts);
    } catch (_) {
      // Background poll fail ho to silently ignore
    }
  }, [activeTab, debouncedSearch]);

  usePounceLeadEvents(handleSocketRefresh);

  // IntersectionObserver: sentinel dikhne par agla batch load karo
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loadingMore || loadMoreError) return;

    // Mobile pe .custom-refresh-wrapper scroller hota hai, desktop pe window
    const getRoot = () => {
      if (typeof window === "undefined") return null;

      const wrapper = document.querySelector(".custom-refresh-wrapper");
      if (!wrapper) return null;

      const style = window.getComputedStyle(wrapper);
      const isScrollable =
        (style.overflowY === "auto" || style.overflowY === "scroll") &&
        wrapper.scrollHeight > wrapper.clientHeight + 1;

      return isScrollable ? wrapper : null;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchMore();
        }
      },
      {
        root: getRoot(),
        rootMargin: "300px",
        threshold: 0,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [leads?.length, hasMore, loadingMore, loadMoreError, fetchMore]);

  const tabs = TABS.map((tab) => ({
    key: tab.key,
    label: `${tab.label} (${tabCounts[tab.key] ?? 0})`,
  }));

  const visible = leads || [];

  return (
    <div className={styles.page}>
      {/* Header + search + tabs ek saath upar chipke rehte hain (.stickyTop). */}
      <div className={styles.stickyTop}>
        <div className={styles.headerSlot}>
          <HeaderBar
            title=""
            subtitle={<OutboundCallerBrand />}
            rightIcon={
              <PounceToggle
                onChange={() => fetchInitial(activeTab, debouncedSearch)}
              />
            }
            onBack={handleBack}
          />
        </div>

        <label className={styles.search}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M16.5 16.5L21 21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, or company..."
            aria-label="Search leads"
          />
        </label>

        <div className={styles.tabsBleed}>
          <PounceLeadsTabs
            tabs={tabs}
            active={activeTab}
            onChange={setActiveTab}
          />
        </div>
      </div>

      {leads === null ? (
        failed ? (
          <div className={styles.state}>
            <p className={styles.stateTitle}>
              {failed === "forbidden"
                ? "You don't have access to leads"
                : "We couldn't load your leads"}
            </p>
            {failed === "forbidden" && (
              <p className={styles.stateText}>
                Ask the account owner to give you call log access.
              </p>
            )}
            <button
              type="button"
              className={styles.retryBtn}
              onClick={() => fetchInitial(activeTab, debouncedSearch)}
            >
              Try again
            </button>
          </div>
        ) : (
          <PounceLeadCardSkeleton />
        )
      ) : visible.length === 0 ? (
        <div className={styles.state}>
          <p className={styles.stateTitle}>
            {debouncedSearch
              ? "No leads match your search"
              : "No leads here yet"}
          </p>
          <p className={styles.stateText}>
            {debouncedSearch
              ? "Try a different name, phone number or company."
              : "When a lead comes in, Outbound Caller calls it within seconds and it shows up here."}
          </p>
        </div>
      ) : (
        <>
          <ul className={styles.list}>
            {visible.map((lead) => (
              <PounceLeadCard key={lead.id} lead={lead} />
            ))}
          </ul>

          {/* Infinite scroll sentinel & status indicators */}
          {hasMore && !loadingMore && !loadMoreError && (
            <div ref={sentinelRef} className={styles.sentinel} />
          )}

          {loadingMore && (
            <div className={styles.loadMoreState}>
              <PounceLeadCardSkeleton />
            </div>
          )}

          {loadMoreError && (
            <div className={styles.loadMoreError}>
              <span>Couldn't load more leads.</span>
              <button
                type="button"
                className={styles.loadMoreRetryBtn}
                onClick={fetchMore}
              >
                Retry
              </button>
            </div>
          )}
        </>
      )}

      {/* Fixed footer: leads tab active */}
      <PounceFooterNav agentId={agentId} active="leads" />
    </div>
  );
};

export default PounceLeadsPage;
